import { DaysIndicator } from '@/components/displays/DaysIndicator';
import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { useSchool, useSession } from '@/providers/session/SessionProvider';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { formatDatePart, formatTimePart } from '@/utils/format.date';
import { useApolloClient } from '@apollo/client';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridFilterModel, GridPaginationModel, GridSortModel, getGridStringOperators, useGridApiRef } from '@mui/x-data-grid';
import { useNotifications } from '@toolpad/core';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { GET_HOMEWORKS, GetHomeworksResponse } from '../../gql';
import { HomeworkData } from '../../types/homework';
import { parseFilterModel } from './ResultsHomeworkTab.filter';

const defaultSortModel: { field: string; sort: 'asc' | 'desc' } = { field: 'endTime', sort: 'desc' };

interface ResultsHomeworkTabProps {
    initialState?: {
        pagination?: { paginationModel: GridPaginationModel };
    };
}

export const ResultsHomeworkTab: React.FC<ResultsHomeworkTabProps> = ({ initialState }) => {
    // provider
    const { t } = useTranslation();
    const { session } = useSession();
    const { school } = useSchool();
    const notifications = useNotifications();
    const navigate = useNavigate();
    const client = useApolloClient();

    // ui
    const [now, setNow] = useState<Date>();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<{ count: number; rows: HomeworkData[] } | undefined>(undefined);
    const [queryOption, setQueryOption] = useState<{ pagination?: GridPaginationModel; sort?: GridSortModel; filter?: GridFilterModel } | undefined>(
        initialState?.pagination?.paginationModel ? { pagination: initialState.pagination.paginationModel } : undefined,
    );
    const gridApiRef = useGridApiRef();

    const columns: GridColDef<HomeworkData>[] = useMemo(
        () => [
            {
                field: 'homeworkName',
                headerName: t('homework.homeworkName'),
                sortable: true,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 160,
                flex: 2,
            },
            {
                field: 'lessonName',
                headerName: t('lesson.label.lessonName'),
                sortable: false,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 160,
                flex: 1,
            },
            {
                field: 'classes',
                headerName: t('class.label.className'),
                sortable: false,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 200,
                flex: 1,
                renderCell: (params) => {
                    return (
                        <Stack
                            direction='row'
                            spacing={1}
                            alignItems='center'
                            sx={{
                                height: '100%',
                            }}
                        >
                            {params.row.classes.map((data, index: number) => {
                                const name = data.className;
                                const avatarColor = generateAvatarColor(name);
                                const bg = avatarColor?.bg;
                                const text = avatarColor?.text;

                                return (
                                    <Stack key={index} direction='row' spacing={1} alignItems='center'>
                                        <Chip
                                            size='medium'
                                            sx={{
                                                '& .MuiChip-avatar': {
                                                    color: text,
                                                },
                                            }}
                                            avatar={
                                                <InitialalsAvatar
                                                    sx={{
                                                        bgcolor: bg,
                                                    }}
                                                >
                                                    {getInitials(name)}
                                                </InitialalsAvatar>
                                            }
                                            label={`${data.className}${data.schoolYear ? ` (${data.schoolYear})` : ``}`}
                                        />
                                    </Stack>
                                );
                            })}
                        </Stack>
                    );
                },
            },
            {
                field: 'endDate',
                headerName: t('homework.deadlineDate'),
                sortable: true,
                filterable: false,
                minWidth: 160,
                valueGetter: (value, row) => row.endTime,
                renderCell: (params) => {
                    return (
                        <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                            <DaysIndicator date={params.value}>
                                <Box>
                                    <Typography variant='body2'>{formatDatePart(params.value)}</Typography>
                                </Box>
                            </DaysIndicator>
                        </Stack>
                    );
                },
            },
            {
                field: 'endTime',
                headerName: t('homework.deadlineTime'),
                sortable: true,
                filterable: false,
                minWidth: 140,
                flex: 1,
                renderCell: (params) => {
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                ...(params.value
                                    ? {
                                          justifyContent: 'space-between',
                                      }
                                    : {
                                          justifyContent: 'flex-end',
                                      }),
                            }}
                        >
                            <Typography variant='body2'>{formatTimePart(params.value)}</Typography>
                            <IconButton size='small'>
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    );
                },
            },
        ],
        [t],
    );

    const refresh = useCallback(async () => {
        if (!queryOption?.pagination || !school?.id || !session?.token || !now) {
            setData(undefined);
            return;
        }

        setLoading(true);

        const filters = queryOption.filter ? parseFilterModel(queryOption.filter) : [];

        await client
            .query<GetHomeworksResponse>({
                query: GET_HOMEWORKS,
                fetchPolicy: 'network-only',
                variables: {
                    skip: queryOption.pagination.page * queryOption.pagination.pageSize,
                    take: queryOption.pagination.pageSize,
                    orderBy: queryOption.sort?.[0] ? [{ [queryOption.sort[0].field]: queryOption.sort[0].sort }] : [{ [defaultSortModel.field]: defaultSortModel.sort }],
                    where: {
                        AND: [
                            {
                                endTime: {
                                    lte: now.toISOString(),
                                },
                            },
                            {
                                class: {
                                    some: {
                                        school: { id: { equals: school?.id } },
                                    },
                                },
                            },
                            ...filters,
                        ],
                    },
                },
            })
            .then((response) => {
                setData({
                    count: response.data.homeworksCount,
                    rows: response.data.homeworks.map((homework) => ({
                        id: homework.id,
                        homeworkName: homework.homeworkName,
                        lessonName: homework.lessonVersion.lessonName,
                        endTime: homework.endTime,
                        classes: homework.class,
                    })),
                });
            })
            .catch((error) => {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            });
        setLoading(false);
    }, [client, notifications, now, queryOption, school?.id, session?.token]);

    /**
     * refresh data when
     *   - pagination model changed
     *   - sort model changed
     *   - school changed
     *   - session changed (not likely)
     */
    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        axios<{ time: string }>({
            method: 'get',
            url: `${import.meta.env.VITE_API_SERVICE_URL}/server`,
        })
            .then((response) => {
                // use server time if possible
                setNow(new Date(response.data.time));
            })
            .catch(() => {
                // fallback to use device time
                setNow(new Date());
            });
    }, []);

    return (
        <DataGrid
            loading={loading}
            columns={columns}
            autoPageSize
            rows={data?.rows}
            filterMode='server'
            sortingMode='server'
            paginationMode='server'
            rowCount={data?.count || 0}
            initialState={{
                pagination: initialState?.pagination,
                sorting: { sortModel: [defaultSortModel] },
            }}
            onPaginationModelChange={(model) => {
                setQueryOption((prev) => (prev ? { ...prev, pagination: model } : { pagination: model }));
                history.replaceState({ ...history.state, ...model }, '');
            }}
            onSortModelChange={(model) => {
                setQueryOption((prev) => (prev ? { ...prev, sort: model } : { sort: model }));
            }}
            onFilterModelChange={(model) => {
                setQueryOption((prev) => (prev ? { ...prev, filter: model } : { filter: model }));
            }}
            sx={{
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                // disable header focus style
                '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                    outline: 'none',
                },
                // disable cell selection style
                '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none',
                },
                // pointer cursor on ALL rows
                '& .MuiDataGrid-row:hover': {
                    cursor: 'pointer',
                },
            }}
            onRowClick={(params) => {
                navigate(`/results/homeworks/${params.row.id}`, { state: { homeworkId: params.row.id }, viewTransition: true });
            }}
            showColumnVerticalBorder={false}
            slotProps={{
                loadingOverlay: {
                    variant: 'linear-progress',
                    noRowsVariant: 'linear-progress',
                },
            }}
            disableRowSelectionOnClick={true}
            hideFooterSelectedRowCount
            apiRef={gridApiRef}
        />
    );
};
